<script setup lang="ts">
import { computed } from 'vue'
import { useAccessStore } from '@/stores/access'
import { useRoute, useRouter } from 'vue-router'
import { Plus } from 'lucide-vue-next'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { can } from '@/composables/useCan'
import { joinsSubProjects, type Kind, type Role } from '@/utils/accessDomains'

const NEW_ROLE = '__new__'

const props = defineProps<{
  kind: Kind
  modelValue: number | null | undefined
  disabled?: boolean
  hideCreate?: boolean
  triggerClass?: string
  projectId?: number | string | null
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: number): void
}>()

const accessStore = useAccessStore()
const route = useRoute()
const router = useRouter()

const roles = computed<Role[]>(() => accessStore.roles(props.kind))
const grantable = (role: Role) => accessStore.grantable(role, props.projectId ?? route.params.project_id as string)

const currentLabel = computed(() => accessStore.role(props.modelValue)?.name ?? 'No role')

const canCreateRole = computed(() => can('org_role', 'create', { orgId: route.params.org_id as string }))

const goToRoles = () => {
  router.push({ name: 'OrgRoles', params: { org_id: route.params.org_id }, query: { scope: props.kind, new: '1' } })
}

const onChange = (value: unknown) => {
  if (value === NEW_ROLE) {
    goToRoles()
    return
  }
  if (typeof value === 'number' && value !== props.modelValue) emit('update:modelValue', value)
}
</script>

<template>
  <Select :model-value="modelValue ?? undefined" :disabled="disabled" @update:model-value="onChange">
    <SelectTrigger :class="triggerClass ?? 'h-7 w-[150px] border-0 bg-transparent px-2 text-sm shadow-none hover:bg-muted'">
      <SelectValue :placeholder="currentLabel">{{ currentLabel }}</SelectValue>
    </SelectTrigger>
    <SelectContent>
      <SelectItem v-for="role in roles" :key="role.role_id" :value="role.role_id" :disabled="!grantable(role)">
        <span class="flex flex-col">
          <span>{{ role.name }}</span>
          <span v-if="kind === 'project' && joinsSubProjects(role)" class="text-xs text-muted-foreground">
            Can join every sub-project
          </span>
        </span>
      </SelectItem>
      <template v-if="canCreateRole && !hideCreate">
        <SelectSeparator />
        <SelectItem :value="NEW_ROLE" class="text-primary">
          <span class="inline-flex items-center gap-1">
            <Plus class="h-3.5 w-3.5" />
            New role
          </span>
        </SelectItem>
      </template>
    </SelectContent>
  </Select>
</template>
